// src/client.ts
var clientConfig = null;
function initClientConfig(config) {
  if (!config.url) {
    throw new Error(
      "API URL is required. Set EXPO_PUBLIC_CF_WORKER_URL in your environment."
    );
  }
  const workerUrl = config.cfWorkerBaseUrl || config.url;
  if (workerUrl && /\bdd-workers(-staging)?\.liftping\.workers\.dev\b/i.test(workerUrl)) {
    console.warn(
      `[api-client] EXPO_PUBLIC_CF_WORKER_URL points at a workers.dev URL (${workerUrl}). Use the custom domain instead (api.dramadetector.com for prod, api-staging.dramadetector.com for staging) \u2014 the workers.dev subdomain can be disabled and on 2026-04-15 the prod one took the app down with "Failed to fetch" until the env var was repointed at the custom domain.`
    );
  }
  clientConfig = config;
}
function getClientConfig() {
  return clientConfig;
}
function resetClientConfig() {
  clientConfig = null;
}

// src/auth/better-auth-adapter.ts
function mapBetterAuthUser(user) {
  return {
    id: user.id,
    email: user.email ?? "",
    name: user.name || void 0,
    image: user.image ?? void 0,
    emailVerified: user.emailVerified ?? false,
    createdAt: user.createdAt instanceof Date ? user.createdAt : new Date(user.createdAt),
    metadata: user.userMetadata
  };
}
function mapBetterAuthSession(data) {
  return {
    user: mapBetterAuthUser(data.user),
    accessToken: data.session.token,
    expiresAt: data.session.expiresAt instanceof Date ? data.session.expiresAt : new Date(data.session.expiresAt)
  };
}
var BetterAuthAdapter = class {
  /**
   * Create a BetterAuthAdapter.
   * @param client - Optional at construction time; must be set via setClient()
   *   before any method is called. This allows the factory to create the adapter
   *   synchronously while the client is configured asynchronously in the app layer.
   */
  constructor(client) {
    this.client = null;
    this.client = client ?? null;
  }
  /**
   * Set or replace the Better Auth client instance.
   * Called from the app layer once the client is created with platform plugins.
   */
  setClient(client) {
    this.client = client;
  }
  getClient() {
    if (!this.client) {
      throw new Error(
        "BetterAuthAdapter: client not initialized. Call setClient() with a configured Better Auth client before using auth methods."
      );
    }
    return this.client;
  }
  async signIn(email, password) {
    const client = this.getClient();
    const result = await client.signIn.email({ email, password });
    if (result.error) throw new Error(result.error.message);
    if (!result.data) throw new Error("Sign-in succeeded but no data returned");
    return mapBetterAuthSession(result.data);
  }
  async signUp(email, password, metadata) {
    const client = this.getClient();
    const result = await client.signUp.email({
      email,
      password,
      name: metadata?.full_name ?? metadata?.name ?? email.split("@")[0],
      ...metadata
    });
    if (result.error) throw new Error(result.error.message);
    if (!result.data) throw new Error("Sign-up succeeded but no data returned");
    return mapBetterAuthSession(result.data);
  }
  async signOut() {
    const client = this.getClient();
    const result = await client.signOut();
    if (result.error) throw new Error(result.error.message);
  }
  async getSession() {
    const client = this.getClient();
    const result = await client.getSession();
    if (result.error) throw new Error(result.error.message);
    if (!result.data) return null;
    return mapBetterAuthSession(result.data);
  }
  async signInWithOAuth(provider) {
    const client = this.getClient();
    const callbackURL = typeof window !== "undefined" ? window.location.origin : "https://www.dramadetector.com";
    const result = await client.signIn.social({ provider, callbackURL });
    if (result.error) throw new Error(result.error.message);
  }
  onAuthStateChange(callback) {
    const client = this.getClient();
    let active = true;
    const unsubscribe = client.onAuthStateChange((data) => {
      if (!active) return;
      callback(data ? mapBetterAuthSession(data) : null);
    });
    return () => {
      active = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }
  async signInAnonymously() {
    const client = this.getClient();
    const result = await client.signIn.anonymous();
    if (result.error) throw new Error(result.error.message);
    if (!result.data) throw new Error("Anonymous sign-in succeeded but no data returned");
    return mapBetterAuthSession(result.data);
  }
};

// src/proxy/edge-url.ts
var _cachedBase = null;
var _cachedConfigRef = null;
function getEdgeFunctionUrl(functionName) {
  const config = getClientConfig();
  if (!config?.cfWorkerBaseUrl) {
    throw new Error(
      "cfWorkerBaseUrl is required. Set EXPO_PUBLIC_CF_WORKER_URL in your environment."
    );
  }
  if (_cachedConfigRef !== config || _cachedBase === null) {
    _cachedBase = config.cfWorkerBaseUrl.replace(/\/+$/, "");
    _cachedConfigRef = config;
  }
  return `${_cachedBase}/${functionName}`;
}
function decodeJwtPayload(token) {
  try {
    const b64url = token.split(".")[1];
    if (!b64url) return null;
    const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}
function getSentryTraceHeaders() {
  const config = getClientConfig();
  if (!config?.getTraceHeaders) return {};
  try {
    const th = config.getTraceHeaders();
    const headers = {};
    if (th["sentry-trace"]) headers["sentry-trace"] = th["sentry-trace"];
    if (th["baggage"]) headers["baggage"] = th["baggage"];
    return headers;
  } catch {
    return {};
  }
}

// src/proxy/auth-token.ts
var _authStatus = "anonymous";
var _pendingReady = null;
var _bootstrapResolve;
var _bootstrapPromise = new Promise((resolve) => {
  _bootstrapResolve = resolve;
});
var _bootstrapped = false;
function setAuthStatus(status) {
  if (!_bootstrapped) {
    _bootstrapped = true;
    _bootstrapResolve();
  }
  _authStatus = status;
  if (status !== "pending" && _pendingReady) {
    _pendingReady.resolve();
    _pendingReady = null;
  }
  if (status === "pending" && !_pendingReady) {
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    _pendingReady = { promise, resolve };
  }
}
function getAuthStatus() {
  return _authStatus;
}
function awaitAuthReady() {
  if (!_pendingReady) return Promise.resolve();
  return _pendingReady.promise;
}
function awaitAuthBootstrap() {
  return _bootstrapPromise;
}
var _getAccessToken = null;
var _refreshAccessToken = null;
function configureAuthToken(getAccessToken, refreshAccessToken) {
  _getAccessToken = getAccessToken ?? null;
  _refreshAccessToken = refreshAccessToken ?? null;
}
function getAccessTokenFn() {
  return _getAccessToken;
}
function getRefreshAccessTokenFn() {
  return _refreshAccessToken;
}
function resetAuthToken() {
  _getAccessToken = null;
  _refreshAccessToken = null;
  _authStatus = "anonymous";
  if (_pendingReady) {
    _pendingReady.resolve();
    _pendingReady = null;
  }
  _bootstrapped = false;
  _bootstrapPromise = new Promise((resolve) => {
    _bootstrapResolve = resolve;
  });
}

// src/utils/authenticated-fetch.ts
var BOOTSTRAP_TIMEOUT_MS = 3e3;
var _onAuthError = null;
var AuthBrokenError = class extends Error {
  constructor(message = "Auth state is broken \u2014 JWT could not be obtained for this session") {
    super(message);
    this.code = "AUTH_BROKEN";
    this.status = 401;
    this.name = "AuthBrokenError";
  }
};
function setOnAuthError(callback) {
  _onAuthError = callback;
}
async function authenticatedFetch(url, options) {
  const config = getClientConfig();
  const skipAuth = options?.skipAuth ?? false;
  const shouldNotifyAuthError = () => getAuthStatus() === "authenticated";
  const buildHeaders = async (token2) => {
    const headers2 = {
      "Accept-Version": "v2",
      ...getSentryTraceHeaders()
    };
    if (process.env.EXPO_PUBLIC_E2E_TEST_MODE === "true") {
      headers2["x-e2e-test"] = "1";
    }
    if (config?.getTelemetryHeaders) {
      try {
        Object.assign(headers2, config.getTelemetryHeaders());
      } catch {
      }
    }
    if (skipAuth) return headers2;
    if (token2) {
      headers2["Authorization"] = `Bearer ${token2}`;
    }
    return headers2;
  };
  if (!skipAuth && getAuthStatus() === "anonymous") {
    let timer;
    await Promise.race([
      awaitAuthBootstrap().finally(() => clearTimeout(timer)),
      new Promise((resolve) => {
        timer = setTimeout(resolve, BOOTSTRAP_TIMEOUT_MS);
      })
    ]);
  }
  if (!skipAuth && getAuthStatus() === "pending") {
    await awaitAuthReady();
  }
  if (!skipAuth && getAuthStatus() === "auth_broken") {
    _onAuthError?.();
    throw new AuthBrokenError();
  }
  const _getAccessToken2 = getAccessTokenFn();
  const _refreshAccessToken2 = getRefreshAccessTokenFn();
  let token = _getAccessToken2?.() ?? null;
  if (!skipAuth && !token && _refreshAccessToken2) {
    try {
      token = await _refreshAccessToken2();
    } catch {
    }
  }
  const { skipAuth: _, ...fetchOptions } = options ?? {};
  const headers = await buildHeaders(token);
  const mergedHeaders = {
    ...headers,
    ...fetchOptions.headers
  };
  const response = await fetch(url, {
    ...fetchOptions,
    headers: mergedHeaders
  });
  if (response.status === 401 && !skipAuth) {
    if (_refreshAccessToken2) {
      try {
        const newToken = await _refreshAccessToken2();
        if (newToken) {
          const retryHeaders = await buildHeaders(newToken);
          const retryMergedHeaders = {
            ...retryHeaders,
            ...fetchOptions.headers
          };
          const retryResponse = await fetch(url, { ...fetchOptions, headers: retryMergedHeaders });
          if (retryResponse.status === 401 && shouldNotifyAuthError()) {
            _onAuthError?.();
          }
          return retryResponse;
        }
      } catch {
      }
    }
    if (shouldNotifyAuthError()) {
      _onAuthError?.();
    }
  }
  return response;
}

// src/utils/error.ts
function ensureStringMessage(value) {
  if (typeof value === "string") {
    return value;
  }
  if (value === null || value === void 0) {
    return "";
  }
  if (typeof value === "object" && value !== null && "message" in value) {
    const nested = value.message;
    return typeof nested === "string" ? nested : String(nested);
  }
  return String(value);
}
function createApiError(code, message, detailsOrOptions) {
  const safeMessage = ensureStringMessage(message);
  if (detailsOrOptions && typeof detailsOrOptions === "object" && ("requestId" in detailsOrOptions || "timestamp" in detailsOrOptions)) {
    const options = detailsOrOptions;
    return {
      code,
      message: safeMessage,
      details: options.details,
      requestId: options.requestId,
      timestamp: options.timestamp
    };
  }
  return {
    code,
    message: safeMessage,
    details: detailsOrOptions
  };
}
function isApiError(error) {
  return typeof error === "object" && error !== null && "code" in error && "message" in error;
}
function getErrorMessage(error) {
  if (isApiError(error)) {
    return ensureStringMessage(error.message);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
function mapSupabaseError(error) {
  const code = error?.code || "UNKNOWN_ERROR";
  const message = error?.message || "An unknown error occurred";
  const errorMap = {
    "invalid_grant": "Invalid credentials",
    "email_not_confirmed": "Email not confirmed",
    "invalid_credentials": "Invalid email or password",
    "user_already_exists": "User already exists",
    "weak_password": "Password is too weak",
    "PGRST116": "Analysis not found"
  };
  return createApiError(
    code,
    errorMap[code] || message,
    error
  );
}

// src/utils/narrative-clarity-platform.ts
function toNarrativeClarityRequest(request) {
  return {
    message: request.message,
    context: request.context
  };
}
function toNarrativeClaritySignal(issue) {
  return {
    type: issue.type,
    rawType: issue.raw_type,
    severity: issue.severity,
    description: issue.description,
    snippet: issue.snippet,
    startIndex: issue.startIndex,
    endIndex: issue.endIndex,
    scoreWeight: issue.scoreWeight
  };
}
function toNarrativeClarityIntervention(rewrite) {
  return {
    kind: "rewrite",
    original: rewrite.original,
    suggested: rewrite.rewritten,
    rationale: rewrite.reasoning,
    improvementAreas: rewrite.improvementAreas
  };
}
function toNarrativeClarityAnalysisEnvelope(request, response, options = {}) {
  const nestedResult = response.result;
  const usingLegacyFallback = !nestedResult;
  const score = nestedResult?.score ?? response.score;
  const category = nestedResult?.category ?? response.category;
  const summary = nestedResult?.summary ?? response.summary;
  const issues = nestedResult?.issues ?? response.issues;
  const rewrites = nestedResult?.rewrites ?? response.rewrites;
  const isSafetyIntercept = response.safety?.crisisDetected === true && score === null;
  const policy = {
    policyKey: options.policy?.policyKey ?? (isSafetyIntercept ? "analysis.safety" : "analysis.default"),
    policyVersion: options.policy?.policyVersion ?? "legacy-runtime",
    decisionMode: options.policy?.decisionMode ?? (isSafetyIntercept ? "blocking" : "advisory"),
    productSurface: options.policy?.productSurface,
    tier: options.policy?.tier
  };
  const provenance = {
    provider: options.provenance?.provider ?? response.metadata.provider,
    model: options.provenance?.model ?? response.metadata.model,
    promptVersion: options.provenance?.promptVersion,
    schemaVersion: options.provenance?.schemaVersion ?? "drama-analysis-response-v1",
    canonicalizationApplied: options.provenance?.canonicalizationApplied ?? (usingLegacyFallback ? ["legacy-flat-response-fallback"] : []),
    fallbackApplied: options.provenance?.fallbackApplied ?? usingLegacyFallback
  };
  return {
    capability: "analysis",
    analysisId: response.id,
    request: toNarrativeClarityRequest(request),
    result: score !== null && category !== null ? {
      score,
      category,
      summary,
      signals: issues.map(toNarrativeClaritySignal),
      interventions: rewrites.map(toNarrativeClarityIntervention)
    } : null,
    safety: response.safety,
    policy,
    provenance,
    metadata: {
      latencyMs: response.metadata.latencyMs,
      cached: response.metadata.cached,
      timestamp: response.metadata.timestamp,
      costUsd: response.metadata.costUsd,
      tokensUsed: response.metadata.tokensUsed
    }
  };
}

// src/api/analysis.ts
function isRetryableNetworkError(error) {
  if (error instanceof DOMException) {
    return error.name === "NetworkError";
  }
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    return message.includes("failed to fetch") || message.includes("load failed") || message.includes("network request failed") || message.includes("fetch failed");
  }
  return false;
}
async function handleEdgeFunctionError(error) {
  if (error.context instanceof Response) {
    try {
      const responseBody = await error.context.json();
      console.log("[analyzeMessage] Edge Function error body:", responseBody);
      if (responseBody.safety?.crisisDetected === true && responseBody.score === null) {
        console.log("[analyzeMessage] Crisis safe response detected (503) - treating as success");
        return {
          data: responseBody,
          error: null
        };
      }
      return {
        data: null,
        error: createApiError(
          responseBody.code || "FUNCTION_ERROR",
          responseBody.message || responseBody.error || "Function request failed",
          {
            details: responseBody,
            requestId: responseBody.requestId,
            timestamp: responseBody.timestamp
          }
        )
      };
    } catch (parseError) {
      console.warn("[analyzeMessage] Failed to parse Edge Function error:", parseError);
      try {
        const rawText = await error.context.text();
        console.error("[analyzeMessage] Raw error response:", rawText?.substring(0, 200));
      } catch {
      }
    }
  }
  return { data: null, error: mapSupabaseError(error) };
}
async function analyzeMessage(request) {
  try {
    const config = getClientConfig();
    const headers = {};
    if (config?.getPlatform) {
      try {
        const platform = config.getPlatform();
        headers["x-platform"] = platform;
      } catch (err) {
        console.warn("[analyzeMessage] Failed to get platform:", err);
      }
    }
    let retryCount = 0;
    let lastError = null;
    const MAX_RETRIES = 2;
    while (retryCount <= MAX_RETRIES) {
      try {
        const functionUrl = getEdgeFunctionUrl("analyze-drama");
        const response = await authenticatedFetch(functionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers
          },
          body: JSON.stringify({
            ...request
          })
        });
        if (!response.ok) {
          return await handleEdgeFunctionError({
            message: `Edge function returned ${response.status}`,
            context: response
          });
        }
        const data = await response.json();
        if (!data || typeof data !== "object") {
          return {
            data: null,
            error: createApiError(
              "INVALID_RESPONSE",
              "Invalid response from analysis service"
            )
          };
        }
        const responseData = data;
        return { data: responseData, error: null };
      } catch (invokeError) {
        lastError = invokeError instanceof Error ? invokeError : new Error(String(invokeError));
        if (isRetryableNetworkError(invokeError) && retryCount < MAX_RETRIES) {
          retryCount++;
          const backoffMs = Math.pow(2, retryCount) * 1e3;
          console.log(`[analyzeMessage] Network error, retrying (${retryCount}/${MAX_RETRIES}) after ${backoffMs}ms`, {
            error: lastError.message,
            online: typeof navigator !== "undefined" ? navigator.onLine : "unknown"
          });
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          continue;
        }
        throw invokeError;
      }
    }
    throw lastError || new Error("Max retries exceeded");
  } catch (err) {
    if (isRetryableNetworkError(err)) {
      if (err instanceof DOMException) {
        console.error("[analyzeMessage] Network error (DOMException):", {
          name: err.name,
          message: err.message,
          code: err.code
        });
      } else {
        console.error("[analyzeMessage] Fetch network error:", err);
      }
      return {
        data: null,
        error: createApiError(
          "NETWORK_ERROR",
          "Unable to reach the analysis service. Please check your internet connection and try again.",
          {
            details: err instanceof Error ? {
              name: err.name,
              message: err.message,
              type: err.constructor.name
            } : err
          }
        )
      };
    }
    console.error("[analyzeMessage] Unexpected error:", err);
    return {
      data: null,
      error: createApiError(
        "ANALYSIS_ERROR",
        "Failed to analyze message",
        err
      )
    };
  }
}
async function analyzeMessageAsNarrativeClarityEnvelope(request, options = {}) {
  const response = await analyzeMessage(request);
  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error
    };
  }
  return {
    data: toNarrativeClarityAnalysisEnvelope(request, response.data, options),
    error: null
  };
}

// src/api/history.ts
var VALID_CATEGORIES = ["safe", "caution", "high_drama", "extreme_drama"];
function validateCategory(value) {
  const normalized = String(value || "").toLowerCase();
  return VALID_CATEGORIES.includes(normalized) ? normalized : "unknown";
}
function buildAnalysisHistoryUrl(params) {
  const queryParams = new URLSearchParams();
  if (params?.limit !== void 0) queryParams.set("limit", String(params.limit));
  if (params?.offset !== void 0) queryParams.set("offset", String(params.offset));
  if (params?.category) queryParams.set("category", params.category);
  const qs = queryParams.toString();
  const base = getEdgeFunctionUrl("get-analysis-history");
  return qs ? `${base}?${qs}` : base;
}
function transformAnalysisRecord(item) {
  try {
    const row = item;
    return {
      id: row.id,
      score: Number(row.score) || 0,
      category: validateCategory(row.category),
      issues: row.issues || [],
      rewrites: row.rewrites || [],
      summary: row.summary || "",
      metadata: row.metadata ?? {
        provider: row.provider,
        model: row.model,
        tokensUsed: row.tokens_used,
        latencyMs: row.latency_ms
      },
      message: row.message || "",
      createdAt: row.created_at ?? row.createdAt ?? "",
      userId: row.user_id ?? row.userId ?? ""
    };
  } catch (err) {
    console.warn("Failed to transform analysis record:", { error: String(err) });
    return null;
  }
}
async function getAnalysisHistory(params) {
  try {
    const edgeUrl = buildAnalysisHistoryUrl(params);
    const response = await authenticatedFetch(edgeUrl);
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      const code = response.status === 401 ? "UNAUTHORIZED" : "HISTORY_ERROR";
      const message = errorBody.error || "Failed to fetch analysis history";
      return { data: null, error: createApiError(code, message, { status: response.status }) };
    }
    const result = await response.json();
    const entries = result.data?.entries ?? [];
    const mapped = entries.map((item) => transformAnalysisRecord(item)).filter((record) => record !== null);
    return { data: mapped, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "HISTORY_ERROR",
        "Failed to fetch analysis history",
        err
      )
    };
  }
}
async function getAnalysisById(id) {
  try {
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("get-analysis-by-id") + "?id=" + encodeURIComponent(id)
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      if (response.status === 404) {
        return {
          data: null,
          error: createApiError("NOT_FOUND", "Analysis not found")
        };
      }
      return {
        data: null,
        error: createApiError(
          "GET_ANALYSIS_ERROR",
          errorBody.error || "Failed to fetch analysis"
        )
      };
    }
    const result = await response.json();
    const item = result.data;
    if (!item) {
      return {
        data: null,
        error: createApiError("NOT_FOUND", "Analysis not found")
      };
    }
    const record = transformAnalysisRecord(item);
    if (!record) {
      return {
        data: null,
        error: createApiError("GET_ANALYSIS_ERROR", "Failed to transform analysis record")
      };
    }
    return { data: record, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "GET_ANALYSIS_ERROR",
        "Failed to fetch analysis",
        err
      )
    };
  }
}
async function deleteAnalysis(id) {
  try {
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("delete-analysis"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      return {
        data: null,
        error: createApiError(
          "DELETE_ANALYSIS_ERROR",
          errorBody.error || "Failed to delete analysis"
        )
      };
    }
    return { data: null, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "DELETE_ANALYSIS_ERROR",
        "Failed to delete analysis",
        err
      )
    };
  }
}
async function clearAllAnalysisHistory() {
  try {
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("clear-analysis-history"),
      { method: "POST" }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      return {
        data: null,
        error: createApiError(
          "CLEAR_HISTORY_ERROR",
          errorBody.error || "Failed to clear analysis history"
        )
      };
    }
    return { data: null, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "CLEAR_HISTORY_ERROR",
        "Failed to clear analysis history",
        err
      )
    };
  }
}
async function getAnalysisStats() {
  try {
    const response = await authenticatedFetch(getEdgeFunctionUrl("get-analysis-stats"));
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        data: null,
        error: createApiError(
          "STATS_ERROR",
          body.error || "Failed to fetch analysis statistics"
        )
      };
    }
    const result = await response.json();
    if (result.error) {
      return { data: null, error: createApiError("STATS_ERROR", result.error.message) };
    }
    if (!result.data) {
      return {
        data: {
          totalAnalyses: 0,
          averageScore: 0,
          categoryBreakdown: {}
        },
        error: null
      };
    }
    return { data: result.data, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "STATS_ERROR",
        "Failed to fetch analysis statistics",
        err
      )
    };
  }
}

// src/api/quota-balance.ts
async function getQuotaBalance() {
  throw new Error(
    "getQuotaBalance is not implemented: get_quota_usage requires (p_scope, p_entity_id, p_period_start) parameters. See NocoDB #2671."
  );
}

// src/api/quota-refund.ts
import { QuotaRefundResultDto } from "@dramadetector/shared/schemas";

// src/utils/fetch-validated.ts
var AUTH_FAILURE_REASON_HEADER = "x-auth-failure-reason";
var DtoValidationError = class extends Error {
  constructor(url, zodIssues, message) {
    super(message);
    this.url = url;
    this.zodIssues = zodIssues;
    this.name = "DtoValidationError";
  }
};
var HttpError = class extends Error {
  constructor(status, url, message, authFailureReason = null) {
    super(message);
    this.status = status;
    this.url = url;
    this.authFailureReason = authFailureReason;
    this.name = "HttpError";
    this.code = status === 401 ? "UNAUTHORIZED" : "HTTP_ERROR";
    this.details = authFailureReason ? { status, authFailureReason } : { status };
  }
};
async function fetchJsonValidated(schema, url, options) {
  const response = await authenticatedFetch(url, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const authFailureReason = response.headers?.get?.(AUTH_FAILURE_REASON_HEADER) ?? null;
    throw new HttpError(
      response.status,
      url,
      err.error ?? `Request failed: ${response.status}`,
      authFailureReason
    );
  }
  const body = await response.json();
  const result = schema.safeParse(body.data);
  if (result.success === true) {
    return result.data;
  }
  const { error } = result;
  throw new DtoValidationError(
    url,
    error.issues,
    `API response did not match expected contract for ${url}: ${error.message}`
  );
}

// src/api/quota-refund.ts
async function requestQuotaRefund(request) {
  try {
    const dto = await fetchJsonValidated(
      QuotaRefundResultDto,
      getEdgeFunctionUrl("request-quota-refund"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          units: request.units || 1,
          reason: request.reason || "error_refund",
          original_ledger_id: request.originalLedgerId || null,
          metadata: request.metadata
        })
      }
    );
    return {
      data: {
        refundLedgerId: dto.refundLedgerId,
        // The DTO models the field as nullable; the legacy API surface uses
        // an `undefined`-or-string convention. Map null → undefined so the
        // public `RefundResult` shape doesn't change for callers.
        originalLedgerId: dto.originalLedgerId ?? void 0,
        units: dto.units
      },
      error: null
    };
  } catch (err) {
    if (err instanceof DtoValidationError) {
      return {
        data: null,
        error: createApiError(
          "REFUND_DTO_INVALID",
          err.message,
          err
        )
      };
    }
    const message = err instanceof Error ? err.message : "Failed to process quota refund";
    if (message.includes("already been refunded")) {
      return {
        data: null,
        error: createApiError(
          "ALREADY_REFUNDED",
          "This ledger entry has already been refunded"
        )
      };
    }
    return {
      data: null,
      error: createApiError("REFUND_ERROR", message, err)
    };
  }
}

// src/api/quota-ledger.ts
async function getQuotaLedgerHistory(params) {
  try {
    const queryParams = new URLSearchParams();
    if (params?.limit !== void 0) queryParams.set("limit", String(params.limit));
    if (params?.offset !== void 0) queryParams.set("offset", String(params.offset));
    if (params?.operation) queryParams.set("operation", params.operation);
    if (params?.source) queryParams.set("source", params.source);
    if (params?.startDate) queryParams.set("startDate", params.startDate);
    if (params?.endDate) queryParams.set("endDate", params.endDate);
    const qs = queryParams.toString();
    const url = `${getEdgeFunctionUrl("get-quota")}${qs ? `?${qs}` : ""}`;
    const response = await authenticatedFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        data: null,
        error: createApiError(
          "LEDGER_HISTORY_ERROR",
          body.error || "Failed to fetch quota ledger history"
        )
      };
    }
    const result = await response.json();
    if (result.error) {
      return { data: null, error: createApiError("LEDGER_HISTORY_ERROR", result.error.message) };
    }
    const rows = result.data || [];
    const entries = rows.map((row) => ({
      id: row.id,
      scope: row.scope,
      entityId: row.entity_id ?? row.entityId,
      units: row.units,
      operation: row.operation,
      source: row.source,
      metadata: row.metadata || {},
      createdBy: row.created_by ?? row.createdBy,
      createdAt: row.created_at ?? row.createdAt
    }));
    return { data: entries, error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "LEDGER_HISTORY_ERROR",
        "Failed to fetch quota ledger history",
        err
      )
    };
  }
}
async function getQuotaLedgerEntry(id) {
  try {
    const url = `${getEdgeFunctionUrl("get-quota")}?id=${encodeURIComponent(id)}`;
    const response = await authenticatedFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        data: null,
        error: createApiError(
          "GET_LEDGER_ENTRY_ERROR",
          body.error || "Failed to fetch ledger entry"
        )
      };
    }
    const result = await response.json();
    if (result.error) {
      return { data: null, error: createApiError("GET_LEDGER_ENTRY_ERROR", result.error.message) };
    }
    if (!result.data) {
      return {
        data: null,
        error: createApiError("NOT_FOUND", "Ledger entry not found")
      };
    }
    const row = result.data;
    return {
      data: {
        id: row.id,
        scope: row.scope,
        entityId: row.entity_id ?? row.entityId,
        units: row.units,
        operation: row.operation,
        source: row.source,
        metadata: row.metadata || {},
        createdBy: row.created_by ?? row.createdBy,
        createdAt: row.created_at ?? row.createdAt
      },
      error: null
    };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "GET_LEDGER_ENTRY_ERROR",
        "Failed to fetch ledger entry",
        err
      )
    };
  }
}

// src/api/tos.ts
async function insertTosAcceptance(tosVersion, acceptanceMethod = "modal") {
  try {
    const response = await authenticatedFetch(getEdgeFunctionUrl("accept-tos"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tos_version: tosVersion,
        acceptance_method: acceptanceMethod
      })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        data: null,
        error: createApiError(
          "TOS_INSERT_ERROR",
          body.error || "Failed to record ToS acceptance"
        )
      };
    }
    const result = await response.json();
    if (result.error) {
      return {
        data: null,
        error: createApiError("TOS_INSERT_ERROR", result.error.message)
      };
    }
    const row = result.data;
    return {
      data: row ? {
        id: row.id ?? row.id,
        userId: row.user_id ?? row.userId,
        tosVersion: row.tos_version ?? row.tosVersion,
        acceptanceMethod: row.acceptance_method ?? row.acceptanceMethod,
        acceptedAt: row.accepted_at ?? row.acceptedAt
      } : null,
      error: null
    };
  } catch (err) {
    return {
      data: null,
      error: createApiError("TOS_INSERT_ERROR", "Failed to record ToS acceptance", err)
    };
  }
}

// src/api/user-stats.ts
async function getUserStatsQuota(userId) {
  try {
    const url = `${getEdgeFunctionUrl("get-user-stats")}?userId=${encodeURIComponent(userId)}`;
    const response = await authenticatedFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        data: null,
        error: createApiError(
          "USER_STATS_ERROR",
          body.error || "Failed to fetch user stats"
        )
      };
    }
    const result = await response.json();
    if (result.error) {
      return {
        data: null,
        error: createApiError("USER_STATS_ERROR", result.error.message)
      };
    }
    const tomorrow = /* @__PURE__ */ new Date();
    tomorrow.setUTCHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const row = result.data;
    return {
      data: {
        dailyAnalysesUsed: row?.daily_analyses_used ?? row?.dailyAnalysesUsed ?? 0,
        dailyResetAt: row?.daily_reset_at ?? row?.dailyResetAt ?? tomorrow.toISOString()
      },
      error: null
    };
  } catch (err) {
    return {
      data: null,
      error: createApiError("USER_STATS_ERROR", "Failed to fetch user stats", err)
    };
  }
}

// src/cache/query-client.ts
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";

// src/cache/query-keys/analysis.ts
var analysisQueryKeys = {
  /**
   * All analysis queries for a specific user
   * @param userId - User ID for cache isolation (required for proper invalidation)
   */
  all: (userId) => ["analysis", userId],
  /**
   * Analysis history list queries (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param params - Query parameters (limit, offset, category)
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  history: (userId, params) => [...analysisQueryKeys.all(userId), "history", params],
  /**
   * Single analysis by ID (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param id - Analysis ID
   */
  detail: (userId, id) => [...analysisQueryKeys.all(userId), "detail", id],
  /**
   * Analysis statistics (USER-SCOPED)
   * @param userId - User ID for cache isolation
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  stats: (userId) => [...analysisQueryKeys.all(userId), "stats"]
};

// src/cache/query-keys/user.ts
var userQueryKeys = {
  /**
   * All user queries
   */
  all: ["user"],
  /**
   * Current user session
   */
  session: () => [...userQueryKeys.all, "session"],
  /**
   * User profile
   */
  profile: () => [...userQueryKeys.all, "profile"],
  /**
   * User quota usage
   */
  quota: () => [...userQueryKeys.all, "quota"]
};

// src/cache/query-keys/generatedContent.ts
var generatedContentQueryKeys = {
  /**
   * All generated content queries for a specific session
   * @param userId - User ID for cache isolation (required)
   */
  all: (userId) => ["generatedContent", userId],
  /**
   * Generated content by session ID (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param sessionId - Session/Request ID from workflow trigger
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  bySession: (userId, sessionId) => [...generatedContentQueryKeys.all(userId), "session", sessionId]
};

// src/cache/query-keys/practitioner.ts
var practitionerQueryKeys = {
  /**
   * All practitioner queries for a specific user
   * @param userId - User ID for cache isolation (SECURITY: REQUIRED)
   */
  all: (userId) => ["practitioner", userId],
  /**
   * User's practitioner groups (many-to-many via join table)
   * @param userId - User ID for cache isolation
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  groups: (userId) => [...practitionerQueryKeys.all(userId), "groups"],
  /**
   * Practitioner group members list
   * @param userId - User ID for cache isolation
   * @param groupId - Practitioner group ID
   */
  members: (userId, groupId) => [...practitionerQueryKeys.all(userId), "members", groupId]
};

// src/cache/query-keys/quotaLedger.ts
var quotaLedgerQueryKeys = {
  /**
   * All quota ledger queries for a specific user
   * @param userId - User ID for cache isolation (SECURITY: REQUIRED)
   */
  all: (userId) => ["quotaLedger", userId],
  /**
   * Quota ledger history (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param params - Query parameters (limit, offset, filters)
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  history: (userId, params) => [...quotaLedgerQueryKeys.all(userId), "history", params],
  /**
   * Single ledger entry by ID (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param id - Ledger entry UUID
   */
  entry: (userId, id) => [...quotaLedgerQueryKeys.all(userId), "entry", id],
  /**
   * Quota balance summary (USER-SCOPED)
   * @param userId - User ID for cache isolation
   */
  balance: (userId) => [...quotaLedgerQueryKeys.all(userId), "balance"]
};

// src/cache/query-keys/crisisAssistance.ts
var crisisAssistanceQueryKeys = {
  /**
   * All crisis assistance queries for a specific user
   * @param userId - User ID for cache isolation (SECURITY: REQUIRED)
   */
  all: (userId) => ["crisisAssistance", userId],
  /**
   * Active crisis requests (USER-SCOPED)
   * @param userId - User ID for cache isolation
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  requests: (userId) => [...crisisAssistanceQueryKeys.all(userId), "requests"],
  /**
   * Single crisis request by ID (USER-SCOPED)
   * @param userId - User ID for cache isolation
   * @param requestId - Crisis request UUID
   */
  request: (userId, requestId) => [...crisisAssistanceQueryKeys.all(userId), "request", requestId]
};

// src/cache/query-keys/crisisConsent.ts
var crisisConsentQueryKeys = {
  /**
   * All crisis consent queries for a specific user
   * @param userId - User ID for cache isolation (SECURITY: REQUIRED)
   */
  all: (userId) => ["crisisConsent", userId],
  /**
   * Practitioners with crisis notification consent (USER-SCOPED)
   * @param userId - User ID for cache isolation
   *
   * SECURITY: userId MUST be provided to prevent cross-user data leakage
   */
  practitioners: (userId) => [...crisisConsentQueryKeys.all(userId), "practitioners"]
};

// src/cache/query-keys/index.ts
var queryKeys = {
  analysis: analysisQueryKeys,
  user: userQueryKeys,
  generatedContent: generatedContentQueryKeys,
  practitioner: practitionerQueryKeys,
  quotaLedger: quotaLedgerQueryKeys,
  crisisAssistance: crisisAssistanceQueryKeys,
  crisisConsent: crisisConsentQueryKeys
};

// src/cache/query-client.ts
var ANALYSIS_QUERY_OPTIONS = {
  refetchOnMount: true,
  // CRITICAL: Force fresh data on mount
  refetchOnWindowFocus: false,
  // Don't refetch on tab focus (UX)
  refetchOnReconnect: true,
  // FIX (2025-12-01): Refetch after network reconnection (P2)
  staleTime: 10 * 60 * 1e3,
  // FIX (2025-12-01): Reduced from 30 to 10 minutes (P3)
  gcTime: 60 * 60 * 1e3
  // 1 hour (matches global)
};
var defaultQueryOptions = {
  queries: {
    // Analysis-Driven Freshness: Cache until next analysis submission
    // Data only refetches when user submits new analysis (via invalidateQueries)
    // 30-min safety net prevents infinite staleness
    staleTime: 30 * 60 * 1e3,
    // 30 minutes (70-80% fewer refetches vs 5 min)
    // Keep unused data in cache for 1 hour
    gcTime: 60 * 60 * 1e3,
    // React Query v5: gcTime replaces cacheTime
    // Retry failed queries 3 times with exponential backoff
    retry: (failureCount, error) => {
      const apiError = error;
      if (apiError.code?.startsWith("4")) {
        return false;
      }
      return failureCount < 3;
    },
    // Exponential backoff: 1s, 2s, 4s
    retryDelay: (attemptIndex) => {
      return Math.min(1e3 * 2 ** attemptIndex, 3e4);
    },
    // Don't refetch on window focus by default
    // (can be overridden per-query for real-time data)
    refetchOnWindowFocus: false,
    // Don't refetch on reconnect by default
    refetchOnReconnect: false,
    // Don't refetch on mount if data is fresh
    refetchOnMount: false,
    // Graceful fallback for orphaned queries restored from cache
    // This prevents "Missing queryFn" errors without crashing the app
    // When a query is restored from cache but its hook hasn't mounted yet,
    // this fallback returns null instead of throwing an error
    queryFn: async ({ queryKey }) => {
      console.warn(
        `[QueryClient] Orphaned query detected, returning null: ${JSON.stringify(queryKey)}`
      );
      return null;
    }
  },
  mutations: {
    // P0 FIX (2026-01-16): Disable mutation retries to prevent double quota deduction
    // Root cause: Network errors trigger automatic retry → second quota charge for NEW messages
    // Trade-off: Users see error UX instead of silent retry (fail fast, correctness > convenience)
    // Industry standard: 10/10 production React Query apps disable mutation retries
    // Rationale: Mutations are user-initiated actions → fail fast → show error → user retries manually
    // Reference: CTO analysis - Double quota deduction bug (agentId: ab973ea)
    retry: false
    // REMOVED: No retry delay needed since retries are disabled
    // retryDelay: (attemptIndex: number) => {
    //   return Math.min(1000 * 2 ** attemptIndex, 5000);
    // },
  }
};
function createQueryClient() {
  return new QueryClient({
    defaultOptions: defaultQueryOptions
  });
}
var mutationKeys = {
  /**
   * Analyze message mutation
   */
  analyzeMessage: ["analyzeMessage"],
  /**
   * Delete analysis mutation
   */
  deleteAnalysis: ["deleteAnalysis"]
};
var CACHE_BUSTER_VERSION = "v3";
var QUERY_CACHE_KEY = `DRAMADETECTOR_QUERY_CACHE_${CACHE_BUSTER_VERSION}_anonymous`;
var PERSISTABLE_QUERY_KEYS = ["analysis", "user"];
var dehydrateOptions = {
  shouldDehydrateQuery: (query) => {
    if (query.state.status !== "success") return false;
    if (query.state.data === void 0) return false;
    const key = query.queryKey[0];
    return PERSISTABLE_QUERY_KEYS.includes(key);
  }
};
function createQueryPersister(userId) {
  return createAsyncStoragePersister({
    storage: AsyncStorage,
    // Include cache buster version in key to invalidate old incompatible caches
    key: `DRAMADETECTOR_QUERY_CACHE_${CACHE_BUSTER_VERSION}_${userId || "anonymous"}`,
    throttleTime: 1e3,
    // Write to storage at most once per second
    serialize: JSON.stringify,
    deserialize: JSON.parse
  });
}

// src/hooks/useAnalyzeMutation.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
function useAnalyzeMutation(options) {
  const queryClient = useQueryClient();
  const {
    userId,
    invalidateHistory = true,
    invalidateStats = true,
    ...mutationOptions
  } = options || {};
  return useMutation({
    mutationKey: mutationKeys.analyzeMessage,
    mutationFn: async (request) => {
      const idempotencyToken = request.idempotencyToken || crypto.randomUUID();
      const response = await analyzeMessage({
        ...request,
        idempotencyToken
      });
      if (response.error) {
        console.error("[useAnalyzeMutation] API error:", response.error);
        throw response.error;
      }
      return response;
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      if (invalidateHistory || invalidateStats) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.analysis.all(userId),
          // ['analysis', userId]
          exact: false
          // Explicitly document partial matching (default behavior)
        });
      }
      const responseData = args[0];
      const quotaData = responseData?.data?.quota;
      if (quotaData && quotaData.used !== void 0) {
        const scope = quotaData.source === "anonymous" ? "device" : quotaData.source;
        const tomorrow = /* @__PURE__ */ new Date();
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        tomorrow.setUTCHours(0, 0, 0, 0);
        const defaultResetAt = tomorrow.toISOString();
        queryClient.setQueriesData(
          { queryKey: ["quota"], exact: false },
          (oldData) => {
            if (!oldData || typeof oldData !== "object") {
              return {
                used: quotaData.used,
                resetAt: quotaData.resetAt || defaultResetAt
              };
            }
            return {
              ...oldData,
              used: quotaData.used,
              resetAt: quotaData.resetAt || defaultResetAt
            };
          }
        );
        console.log("[useAnalyzeMutation] Quota cache updated from API response:", {
          used: quotaData.used,
          limit: quotaData.limit,
          remaining: quotaData.remaining,
          source: quotaData.source
        });
        queryClient.invalidateQueries({
          queryKey: ["quota"],
          exact: false
          // Match all quota queries with partial key matching
          // refetchType: 'active' (default) immediately refetches mounted observers
        });
      }
      mutationOptions.onSuccess?.(...args);
    },
    onError: (error, ...args) => {
      console.error("[useAnalyzeMutation] Mutation error:", error);
      mutationOptions.onError?.(error, ...args);
    }
  });
}

// src/hooks/useAnalysisHistory.ts
import { useQuery } from "@tanstack/react-query";
import {
  AnalysisHistoryPageDto
} from "@dramadetector/shared/schemas";
function useAnalysisHistory({ userId, params, ...options } = {}) {
  return useQuery({
    // SECURITY (2025-12-01): User-scoped query key prevents cross-user data leakage.
    queryKey: queryKeys.analysis.history(userId, params),
    queryFn: async () => {
      const page = await fetchJsonValidated(
        AnalysisHistoryPageDto,
        buildAnalysisHistoryUrl(params)
      );
      return page.entries;
    },
    ...ANALYSIS_QUERY_OPTIONS,
    ...options
  });
}

// src/hooks/useInfiniteAnalysisHistory.ts
import { useInfiniteQuery } from "@tanstack/react-query";
import { AnalysisHistoryPageDto as AnalysisHistoryPageDto2 } from "@dramadetector/shared/schemas";
var DEFAULT_PAGE_SIZE = 20;
function useInfiniteAnalysisHistory(options) {
  const { userId, pageSize = DEFAULT_PAGE_SIZE, category, enabled } = options || {};
  return useInfiniteQuery({
    // User-scoped query key with pagination params
    queryKey: queryKeys.analysis.history(userId, { pageSize, category, infinite: true }),
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam : 0;
      const page = await fetchJsonValidated(
        AnalysisHistoryPageDto2,
        buildAnalysisHistoryUrl({ limit: pageSize, offset, category })
      );
      return page.entries;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return void 0;
      }
      return allPages.length * pageSize;
    },
    // Use standardized options — refetchOnMount: true is CRITICAL for data freshness.
    //
    // HISTORY (2025-12-14): This was previously overridden to `refetchOnMount: false`
    // to avoid refetching all loaded pages on mount. However, this caused NocoDB #2858:
    // after a new analysis, navigating to the History tab showed empty/stale data because
    // invalidateQueries only marks inactive queries as stale — with refetchOnMount: false,
    // stale queries never refetch on mount.
    //
    // The performance concern (refetching all pages) is mitigated by React Query's
    // invalidation behavior: only the first page refetches initially, and subsequent
    // pages refetch lazily when scrolled to.
    ...ANALYSIS_QUERY_OPTIONS,
    enabled: enabled ?? true
  });
}

// src/hooks/useAnalysisStats.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
import { AnalysisStatsDto } from "@dramadetector/shared/schemas";
function useAnalysisStats(options) {
  const { userId, enabled, ...queryOptions } = options || {};
  return useQuery2({
    // SECURITY FIX (2025-12-01): User-scoped query key prevents cross-user data leakage
    // Without userId, User B would see User A's cached data after logout/login
    // Multi-model consensus: Gemini Pro 3 (10/10), o3 (8/10) confirmed this approach
    queryKey: queryKeys.analysis.stats(userId),
    queryFn: async () => {
      return fetchJsonValidated(
        AnalysisStatsDto,
        getEdgeFunctionUrl("get-analysis-stats")
      );
    },
    // FIX (2025-11-29): Use standardized ANALYSIS_QUERY_OPTIONS
    // - refetchOnMount: true prevents stale cache when tab not mounted during invalidation
    // - refetchOnWindowFocus: false prevents unnecessary background fetches
    // See query-client.ts for full documentation and multi-model consensus details
    ...ANALYSIS_QUERY_OPTIONS,
    // FIX (2025-12-01): Auth guard to prevent race condition
    // Query won't fire until enabled is true (default: true for backward compatibility)
    // Consumers should pass enabled: !!user from auth context
    enabled: enabled ?? true,
    // Allow user options to override if needed
    ...queryOptions
  });
}

// src/hooks/useQuotaLedger.ts
import { useQuery as useQuery3 } from "@tanstack/react-query";
function useQuotaLedgerHistory(options) {
  const { userId, params, enabled, ...queryOptions } = options || {};
  return useQuery3({
    // SECURITY: User-scoped query key prevents cross-user data leakage
    queryKey: queryKeys.quotaLedger.history(userId, params),
    queryFn: async () => {
      const response = await getQuotaLedgerHistory(params);
      if (response.error) {
        throw response.error;
      }
      return response.data || [];
    },
    // Use standardized query options
    ...ANALYSIS_QUERY_OPTIONS,
    // Auth guard to prevent race condition
    enabled: enabled ?? true,
    // Allow user options to override
    ...queryOptions
  });
}
function useQuotaBalance(options) {
  const { userId, enabled, ...queryOptions } = options || {};
  return useQuery3({
    queryKey: queryKeys.quotaLedger.balance(userId),
    queryFn: async () => {
      const response = await getQuotaBalance();
      if (response.error) {
        throw response.error;
      }
      if (!response.data) {
        throw new Error("No balance data returned");
      }
      return response.data;
    },
    ...ANALYSIS_QUERY_OPTIONS,
    enabled: enabled ?? true,
    ...queryOptions
  });
}
function useQuotaLedgerEntry(options) {
  const { userId, id, enabled, ...queryOptions } = options;
  return useQuery3({
    queryKey: queryKeys.quotaLedger.entry(userId, id),
    queryFn: async () => {
      const response = await getQuotaLedgerEntry(id);
      if (response.error) {
        throw response.error;
      }
      if (!response.data) {
        throw new Error("Ledger entry not found");
      }
      return response.data;
    },
    ...ANALYSIS_QUERY_OPTIONS,
    enabled: enabled ?? true,
    ...queryOptions
  });
}

// src/hooks/usePractitionerGroups.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";

// src/hooks/useAuthReady.ts
function useAuthReady() {
  return getAuthStatus() === "authenticated";
}

// src/hooks/usePractitionerGroups.ts
async function fetchPractitionerGroups() {
  return [];
}
function usePractitionerGroups(userId, enabled = true) {
  const authReady = useAuthReady();
  return useQuery4({
    // SECURITY: Include userId in query key (TC_SEC_1 prevention)
    queryKey: queryKeys.practitioner.groups(userId),
    queryFn: async () => {
      if (!userId) {
        throw new Error("userId is required");
      }
      return fetchPractitionerGroups();
    },
    // Only run query if userId provided, caller-enabled, AND auth is ready.
    // See `useAuthReady` for the JWT-pending race context (NocoDB #3624).
    enabled: enabled && !!userId && authReady,
    // Apply ANALYSIS_QUERY_OPTIONS pattern
    // - refetchOnMount: true -> Prevents stale data when navigating to Groups tab
    // - refetchOnReconnect: true -> Fresh data after network reconnection (mobile)
    // - staleTime: 10 min -> Balance between freshness and API calls
    ...ANALYSIS_QUERY_OPTIONS
  });
}

// src/hooks/useGeneratedContent.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";

// src/api/generated-content.ts
import {
  GeneratedContentResultDto
} from "@dramadetector/shared/schemas";
function mergeGeneratedContent(rows) {
  const completedRows = rows.filter((row) => row.status === "completed" && row.output != null).sort((a, b) => b.created_at.localeCompare(a.created_at));
  const merged = {};
  for (const row of completedRows) {
    const name = row.agent_name.toLowerCase();
    const output = row.output;
    if (!merged.sharecopy && (name.includes("sharecopy") || name.includes("share_copy"))) {
      const variants = output?.variants;
      if (Array.isArray(variants)) {
        merged.sharecopy = { variants };
      }
    } else if (!merged.tooltip && name.includes("tooltip")) {
      const tooltips = output?.tooltips;
      if (Array.isArray(tooltips)) {
        merged.tooltip = { tooltips };
      }
    } else if (!merged.lesson && name.includes("lesson")) {
      const lesson = output?.lesson;
      if (lesson && typeof lesson === "object") {
        merged.lesson = { lesson };
      }
    } else if (!merged.insight && name.includes("insight")) {
      const insights = output?.insights;
      if (Array.isArray(insights)) {
        merged.insight = { insights };
      }
    }
  }
  if (completedRows[0]) {
    merged.generatedAt = completedRows[0].created_at;
  }
  return merged;
}
async function getGeneratedContent(sessionId) {
  try {
    const url = `${getEdgeFunctionUrl("get-generated-content")}?analysisId=${encodeURIComponent(sessionId)}`;
    const rows = await fetchJsonValidated(GeneratedContentResultDto, url);
    if (rows.length === 0) {
      return { data: null, error: null };
    }
    return { data: mergeGeneratedContent(rows), error: null };
  } catch (err) {
    if (err instanceof DtoValidationError) {
      return {
        data: null,
        error: createApiError("GENERATED_CONTENT_DTO_INVALID", err.message, err)
      };
    }
    return {
      data: null,
      error: createApiError(
        "GENERATED_CONTENT_ERROR",
        err instanceof Error ? err.message : "Failed to fetch generated content",
        err
      )
    };
  }
}

// src/hooks/useGeneratedContent.ts
var POLLING_CONFIG = {
  /** Initial poll interval (1 second) */
  INITIAL_INTERVAL: 1e3,
  /** Max poll interval with backoff (5 seconds) */
  MAX_INTERVAL: 5e3,
  /** Max polling duration (30 seconds) */
  MAX_DURATION: 3e4,
  /** Backoff multiplier */
  BACKOFF_MULTIPLIER: 1.5
};
function useGeneratedContent(options) {
  const { userId, sessionId, pollUntilReady = false, enabled = true, ...queryOptions } = options;
  const query = useQuery5({
    queryKey: queryKeys.generatedContent.bySession(userId, sessionId),
    queryFn: async () => {
      const response = await getGeneratedContent(sessionId);
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    enabled: enabled && !!sessionId,
    // Polling configuration
    refetchInterval: pollUntilReady ? (query2) => {
      if (query2.state.data) {
        return false;
      }
      return POLLING_CONFIG.INITIAL_INTERVAL;
    } : false,
    // Don't refetch on window focus (we control refetching via polling)
    refetchOnWindowFocus: false,
    // 5 minute stale time for workflow content
    staleTime: 5 * 60 * 1e3,
    // Allow user options to override
    ...queryOptions
  });
  const hasContent = query.data !== null && query.data !== void 0 && Object.keys(query.data).length > 0;
  const contentSource = query.isLoading ? "loading" : hasContent ? "workflow" : "template";
  return {
    ...query,
    hasContent,
    contentSource
  };
}

// src/api/revisions-types.ts
function mapRevisionRecord(row) {
  return {
    id: row.id,
    analysisId: row.analysis_id,
    userId: row.user_id,
    revisionNumber: row.revision_number,
    status: row.status,
    originalMessage: row.original_message,
    originalScore: row.original_score,
    originalCategory: row.original_category,
    originalIssues: row.original_issues,
    originalSummary: row.original_summary,
    revisedMessage: row.revised_message,
    revisedScore: row.revised_score,
    revisedCategory: row.revised_category,
    revisedIssues: row.revised_issues,
    revisedSummary: row.revised_summary,
    scoreDelta: row.score_delta,
    issuesResolved: row.issues_resolved,
    issuesTotal: row.issues_total,
    agentExecutionId: row.agent_execution_id,
    modelId: row.model_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    completedAt: row.completed_at
  };
}
function mapRewriteEventRecord(row) {
  return {
    id: row.id,
    revisionId: row.revision_id,
    issueId: row.issue_id,
    issueType: row.issue_type,
    severity: row.severity,
    snippet: row.snippet,
    replacement: row.replacement,
    startIndex: row.start_index,
    endIndex: row.end_index,
    decision: row.decision,
    userModifiedText: row.user_modified_text,
    decidedAt: row.decided_at,
    decisionLatencyMs: row.decision_latency_ms,
    suggestionAgentId: row.suggestion_agent_id,
    suggestionProvider: row.suggestion_provider,
    suggestionModel: row.suggestion_model,
    suggestionTokens: row.suggestion_tokens,
    suggestionCostUsd: row.suggestion_cost_usd,
    createdAt: row.created_at
  };
}

// src/api/revisions.ts
var UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
async function createRevision(input) {
  try {
    if (!UUID_REGEX.test(input.analysisId)) {
      return {
        data: null,
        error: createApiError("INVALID_INPUT", "analysisId must be a valid UUID")
      };
    }
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("create-revision"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          analysis_id: input.analysisId,
          original_message: input.originalMessage,
          original_score: input.originalScore,
          original_category: input.originalCategory,
          original_issues: input.originalIssues,
          original_summary: input.originalSummary,
          agent_execution_id: input.agentExecutionId,
          model_id: input.modelId,
          issues_total: input.issuesTotal || input.originalIssues?.length || 0
        })
      }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      const code = errorBody.code || "CREATE_REVISION_ERROR";
      if (code === "ANALYSIS_NOT_FOUND" || errorBody.error?.includes("not found")) {
        return {
          data: null,
          error: createApiError(
            "ANALYSIS_NOT_FOUND",
            "Analysis not found in database - revision tracking skipped for cached/anonymous analyses"
          )
        };
      }
      return {
        data: null,
        error: createApiError(code, errorBody.error || "Failed to create revision session")
      };
    }
    const result = await response.json();
    return { data: mapRevisionRecord(result.data), error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "CREATE_REVISION_ERROR",
        "Failed to create revision session",
        err
      )
    };
  }
}
async function createRewriteEvent(input) {
  try {
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("create-rewrite-event"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          revision_id: input.revisionId,
          issue_id: input.issueId,
          issue_type: input.issueType,
          severity: input.severity,
          snippet: input.snippet,
          replacement: input.replacement,
          start_index: input.startIndex,
          end_index: input.endIndex,
          decision: input.decision,
          user_modified_text: input.userModifiedText,
          decision_latency_ms: input.decisionLatencyMs,
          suggestion_agent_id: input.suggestionAgentId,
          suggestion_provider: input.suggestionProvider,
          suggestion_model: input.suggestionModel,
          suggestion_tokens: input.suggestionTokens,
          suggestion_cost_usd: input.suggestionCostUsd
        })
      }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      return {
        data: null,
        error: createApiError(
          "CREATE_REWRITE_EVENT_ERROR",
          errorBody.error || "Failed to create rewrite event"
        )
      };
    }
    const result = await response.json();
    return { data: mapRewriteEventRecord(result.data), error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "CREATE_REWRITE_EVENT_ERROR",
        "Failed to create rewrite event",
        err
      )
    };
  }
}
async function completeRevision(revisionId, input, status = "completed") {
  try {
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("complete-revision"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          revision_id: revisionId,
          revised_message: input.revisedMessage,
          revised_score: input.revisedScore,
          revised_category: input.revisedCategory,
          revised_issues: input.revisedIssues,
          revised_summary: input.revisedSummary,
          issues_resolved: input.issuesResolved,
          status
        })
      }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      return {
        data: null,
        error: createApiError(
          "COMPLETE_REVISION_ERROR",
          errorBody.error || "Failed to complete revision"
        )
      };
    }
    const result = await response.json();
    return { data: mapRevisionRecord(result.data), error: null };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "COMPLETE_REVISION_ERROR",
        "Failed to complete revision",
        err
      )
    };
  }
}
async function rescoreAnalysis(input) {
  try {
    if (!UUID_REGEX.test(input.analysisId)) {
      return {
        data: null,
        error: createApiError("INVALID_INPUT", "analysisId must be a valid UUID")
      };
    }
    const response = await authenticatedFetch(
      getEdgeFunctionUrl("rescore-drama"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          analysis_id: input.analysisId,
          revised_message: input.revisedMessage,
          original_score: input.originalScore,
          context: input.context
        })
      }
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: response.statusText }));
      return {
        data: null,
        error: createApiError(
          "RESCORE_ERROR",
          errorBody.error || `Edge function returned ${response.status}`
        )
      };
    }
    const body = await response.json();
    const payload = body && typeof body === "object" && "data" in body ? body.data : null;
    if (payload && typeof payload === "object" && "score" in payload && "category" in payload) {
      const typed = payload;
      return {
        data: { success: true, score: typed.score, category: typed.category },
        error: null
      };
    }
    return {
      data: null,
      error: createApiError("RESCORE_ERROR", "Invalid response from rescore endpoint")
    };
  } catch (err) {
    return {
      data: null,
      error: createApiError(
        "RESCORE_ERROR",
        "Failed to rescore analysis",
        err
      )
    };
  }
}

// src/hooks/usePractitionerInvite.ts
import { useMutation as useMutation2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
import {
  PractitionerInviteResultDto
} from "@dramadetector/shared/schemas";

// src/utils/call-edge-function.ts
function onMutationError(hookName, fnName) {
  return (error) => {
    console.error(`[${hookName}] Mutation failed:`, error);
    getClientConfig()?.captureException?.(error, {
      tags: { hook: hookName, function: fnName }
    });
  };
}

// src/hooks/usePractitionerInvite.ts
async function invitePractitionerMember(request) {
  return fetchJsonValidated(
    PractitionerInviteResultDto,
    getEdgeFunctionUrl("practitioner-invite"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    }
  );
}
function usePractitionerInvite(userId) {
  const queryClient = useQueryClient2();
  return useMutation2({
    mutationFn: (request) => invitePractitionerMember(request),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.practitioner.all(userId)
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["practitioner"]
        });
      }
    },
    onError: onMutationError("usePractitionerInvite", "practitioner-invite")
  });
}

// src/hooks/usePractitionerRemove.ts
import { useMutation as useMutation3, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
import {
  PractitionerRemoveResultDto
} from "@dramadetector/shared/schemas";
async function removePractitionerMember(request) {
  return fetchJsonValidated(
    PractitionerRemoveResultDto,
    getEdgeFunctionUrl("practitioner-remove"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    }
  );
}
function usePractitionerRemove(userId) {
  const queryClient = useQueryClient3();
  return useMutation3({
    mutationFn: (request) => removePractitionerMember(request),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.practitioner.all(userId)
        });
      } else {
        console.warn(
          "[usePractitionerRemove] userId not provided - invalidating all practitioner queries"
        );
        queryClient.invalidateQueries({
          queryKey: ["practitioner"]
        });
      }
    },
    onError: onMutationError("usePractitionerRemove", "practitioner-remove")
  });
}

// src/hooks/useDeleteUserData.ts
import { useMutation as useMutation4 } from "@tanstack/react-query";
import {
  DeleteUserDataResultDto
} from "@dramadetector/shared/schemas";
async function deleteUserData(options) {
  return fetchJsonValidated(
    DeleteUserDataResultDto,
    getEdgeFunctionUrl("delete-user-data"),
    {
      method: "POST",
      ...options
    }
  );
}
function useDeleteUserData() {
  return useMutation4({
    mutationFn: () => deleteUserData()
  });
}

// src/hooks/useCrisisAssistance.ts
import { useMutation as useMutation5, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
import {
  CrisisAssistanceRequestResultDto
} from "@dramadetector/shared/schemas";
var CRISIS_REQUEST_TIMEOUT_MS = 1e4;
async function createCrisisAssistanceRequest(request) {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    CRISIS_REQUEST_TIMEOUT_MS
  );
  try {
    return await fetchJsonValidated(
      CrisisAssistanceRequestResultDto,
      getEdgeFunctionUrl("crisis-assistance-request"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: controller.signal
      }
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(
        "Request timed out. Please check your connection and try again."
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
function useCrisisAssistance(userId) {
  const queryClient = useQueryClient4();
  return useMutation5({
    mutationFn: (request) => createCrisisAssistanceRequest(request),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.crisisAssistance.all(userId)
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["crisisAssistance"]
        });
      }
    },
    onError: onMutationError("useCrisisAssistance", "crisis-assistance-request")
  });
}

// src/hooks/usePractitionerCrisisConsent.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
import {
  PractitionerConsentListDto
} from "@dramadetector/shared/schemas";
async function fetchPractitionerCrisisConsent(_patientId) {
  const url = `${getEdgeFunctionUrl("get-practitioner-consent")}?allow_crisis=true`;
  return fetchJsonValidated(PractitionerConsentListDto, url);
}
function usePractitionerCrisisConsent(userId, enabled = true) {
  return useQuery6({
    // SECURITY: Include userId in query key (TC_SEC_1 prevention)
    queryKey: queryKeys.crisisConsent.practitioners(userId),
    queryFn: async () => {
      if (!userId) {
        throw new Error("userId is required");
      }
      return fetchPractitionerCrisisConsent(userId);
    },
    // Only run query if userId provided and enabled
    enabled: enabled && !!userId,
    // Apply ANALYSIS_QUERY_OPTIONS pattern
    // - refetchOnMount: true → Fresh data when entering crisis flow
    // - refetchOnReconnect: true → Fresh data after network reconnection
    // - staleTime: 10 min → Balance between freshness and API calls
    ...ANALYSIS_QUERY_OPTIONS
  });
}

// src/hooks/messaging/useConversations.ts
import { useCallback } from "react";
import { useInfiniteQuery as useInfiniteQuery2 } from "@tanstack/react-query";
import {
  ListConversationsPageDto
} from "@dramadetector/shared/schemas";
var DEFAULT_CONVERSATIONS_PAGE_SIZE = 20;
function mapConversationListEntryToConversation(entry) {
  const status = entry.status === "active" || entry.status === "archived" || entry.status === "deleted" ? entry.status : "active";
  return {
    id: entry.id,
    user_id: "",
    title: entry.title,
    status,
    tier_required: entry.tierRequired,
    last_message_at: entry.lastMessageAt,
    created_at: entry.createdAt,
    updated_at: entry.updatedAt
  };
}
async function fetchConversationsPage(cursor, pageSize) {
  const params = new URLSearchParams();
  params.set("limit", String(pageSize));
  if (cursor) params.set("cursor", cursor);
  const url = `${getEdgeFunctionUrl("list-conversations")}?${params.toString()}`;
  const page = await fetchJsonValidated(ListConversationsPageDto, url);
  return {
    conversations: page.conversations.map(mapConversationListEntryToConversation),
    nextCursor: page.nextCursor
  };
}
function useConversations(options) {
  const { pageSize = DEFAULT_CONVERSATIONS_PAGE_SIZE, enabled = true } = options ?? {};
  const query = useInfiniteQuery2({
    queryKey: ["messaging", "conversations"],
    queryFn: ({ pageParam }) => fetchConversationsPage(pageParam, pageSize),
    initialPageParam: null,
    getNextPageParam: (lastPage2) => lastPage2.nextCursor ?? void 0,
    staleTime: 1e4,
    enabled
  });
  const conversations = query.data?.pages.flatMap((p) => p.conversations) ?? [];
  const lastPage = query.data?.pages[query.data.pages.length - 1];
  const nextCursor = lastPage?.nextCursor ?? null;
  const fetchNextPage = useCallback(() => {
    if (query.hasNextPage) query.fetchNextPage();
  }, [query.hasNextPage, query.fetchNextPage]);
  const refetch = useCallback(() => {
    query.refetch();
  }, [query.refetch]);
  return {
    conversations,
    nextCursor,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage,
    refetch,
    error: query.error ?? null
  };
}

// src/hooks/messaging/useMessages.ts
import { useCallback as useCallback2, useRef } from "react";
import { useQuery as useQuery7 } from "@tanstack/react-query";
import { GetMessagesPageDto } from "@dramadetector/shared/schemas";

// src/hooks/messaging/api-utils.ts
async function throwMessagingError(response, fallbackCode, fallbackMessage) {
  const body = await response.json().catch(() => ({}));
  throw createApiError(
    body.code ?? (response.status === 401 ? "UNAUTHORIZED" : fallbackCode),
    body.error ?? fallbackMessage,
    { status: response.status }
  );
}
function mapMessageDtoToMessage(dto) {
  return {
    id: dto.id,
    conversation_id: dto.conversationId,
    user_id: dto.userId,
    content: dto.content,
    drama_score: dto.dramaScore === null ? null : String(dto.dramaScore),
    narrator_commentary: dto.narratorCommentary,
    issues: dto.issues ?? [],
    suggested_rewrites: dto.suggestedRewrites ?? [],
    created_at: dto.createdAt
  };
}

// src/hooks/messaging/useMessages.ts
async function fetchMessages(conversationId, mode, limit, since, before) {
  const params = new URLSearchParams();
  params.set("conversationId", conversationId);
  params.set("limit", String(limit));
  if (mode === "history") {
    params.set("direction", "history");
    if (before) params.set("before", before);
  } else if (since) {
    params.set("since", since);
  }
  const url = `${getEdgeFunctionUrl("get-messages")}?${params.toString()}`;
  const page = await fetchJsonValidated(GetMessagesPageDto, url);
  return {
    messages: page.messages.map(mapMessageDtoToMessage),
    hasMore: page.hasMore,
    nextCursor: page.nextCursor
  };
}
function useMessages(options) {
  const {
    conversationId,
    mode = "thread",
    pollIntervalMs = 2e3,
    limit = 50,
    before,
    enabled = true
  } = options;
  const latestCreatedAtRef = useRef(null);
  const accumulatedMessagesRef = useRef([]);
  const queryKey = mode === "history" ? ["messaging", "messages", conversationId, "history", before ?? null] : ["messaging", "messages", conversationId];
  const query = useQuery7({
    queryKey,
    queryFn: async () => {
      const since = mode === "thread" ? latestCreatedAtRef.current : null;
      const result = await fetchMessages(conversationId, mode, limit, since, before);
      if (mode === "thread") {
        if (since === null) {
          accumulatedMessagesRef.current = result.messages;
        } else if (result.messages.length > 0) {
          const seen = new Set(accumulatedMessagesRef.current.map((m) => m.id));
          const newOnes = result.messages.filter((m) => !seen.has(m.id));
          accumulatedMessagesRef.current = [
            ...accumulatedMessagesRef.current,
            ...newOnes
          ];
        }
        const all = accumulatedMessagesRef.current;
        if (all.length > 0) {
          latestCreatedAtRef.current = all.reduce(
            (max, m) => m.created_at > max ? m.created_at : max,
            latestCreatedAtRef.current ?? ""
          );
        }
        return {
          messages: accumulatedMessagesRef.current,
          hasMore: result.hasMore,
          nextCursor: result.nextCursor
        };
      }
      return result;
    },
    enabled: enabled && Boolean(conversationId),
    refetchInterval: mode === "thread" ? pollIntervalMs : false,
    refetchIntervalInBackground: false,
    staleTime: mode === "thread" ? 0 : 3e4
  });
  const refetch = useCallback2(() => {
    latestCreatedAtRef.current = null;
    accumulatedMessagesRef.current = [];
    query.refetch();
  }, [query.refetch]);
  return {
    messages: query.data?.messages ?? [],
    hasMore: query.data?.hasMore ?? false,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch
  };
}

// src/hooks/messaging/useSendMessage.ts
import { useCallback as useCallback3 } from "react";
import { useMutation as useMutation6, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
import { SendMessageResultDto } from "@dramadetector/shared/schemas";
async function postSendMessage(input) {
  const response = await authenticatedFetch(getEdgeFunctionUrl("send-message"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversationId: input.conversationId,
      content: input.content,
      context: input.context,
      clientMessageId: input.clientMessageId
    })
  });
  if (!response.ok) {
    await throwMessagingError(response, "SEND_MESSAGE_ERROR", "Failed to send message");
  }
  const body = await response.json();
  const parsed = SendMessageResultDto.safeParse(body.data);
  if (!parsed.success) {
    throw new Error(
      `send-message response did not match expected contract: ${parsed.error.message}`
    );
  }
  if (parsed.data.status === "pending") {
    throw Object.assign(new Error("Send still in progress \u2014 retry pending"), {
      code: "SEND_MESSAGE_PENDING",
      details: { status: response.status, retryAfter: parsed.data.retryAfter }
    });
  }
  return mapMessageDtoToMessage(parsed.data.message);
}
function generateClientMessageId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `cli_${crypto.randomUUID()}`;
  }
  return `cli_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
function useSendMessage(options = {}) {
  const queryClient = useQueryClient5();
  const { onAnalyticsEvent } = options;
  const emitAnalytics = (eventName, properties) => {
    if (!onAnalyticsEvent) return;
    try {
      onAnalyticsEvent(eventName, properties);
    } catch {
    }
  };
  const mutation = useMutation6({
    mutationFn: (input) => postSendMessage(input),
    onMutate: async (input) => {
      const messagesKey = ["messaging", "messages", input.conversationId];
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const previousMessages = queryClient.getQueryData(messagesKey);
      const clientMessageId = input.clientMessageId ?? generateClientMessageId();
      const nowIso = (/* @__PURE__ */ new Date()).toISOString();
      const optimisticMessage = {
        id: clientMessageId,
        // temporary — replaced by server id onSuccess
        conversation_id: input.conversationId,
        user_id: "self",
        // UI will render "me"; real id arrives onSuccess
        content: input.content,
        drama_score: null,
        narrator_commentary: null,
        issues: [],
        suggested_rewrites: [],
        created_at: nowIso,
        clientMessageId,
        status: "sending"
      };
      queryClient.setQueryData(messagesKey, (old) => {
        const existing = old?.messages ?? [];
        return {
          messages: [...existing, optimisticMessage],
          hasMore: old?.hasMore ?? false
        };
      });
      return {
        clientMessageId,
        conversationId: input.conversationId,
        previousMessages
      };
    },
    onSuccess: (serverMessage, _input, context) => {
      if (!context) return;
      const messagesKey = [
        "messaging",
        "messages",
        context.conversationId
      ];
      queryClient.setQueryData(messagesKey, (old) => {
        const existing = old?.messages ?? [];
        const replaced = existing.map((m) => {
          if (m.clientMessageId === context.clientMessageId) {
            return {
              ...serverMessage,
              clientMessageId: context.clientMessageId,
              status: "sent"
            };
          }
          return m;
        });
        return { messages: replaced, hasMore: old?.hasMore ?? false };
      });
      queryClient.invalidateQueries({
        queryKey: ["messaging", "conversations"]
      });
      emitAnalytics("messaging_message_sent", {
        conversation_id: context.conversationId,
        message_id: serverMessage.id,
        drama_score: serverMessage.drama_score ?? void 0,
        message_length: serverMessage.content?.length ?? 0
      });
    },
    onError: (error, input, context) => {
      if (!context) return;
      const messagesKey = [
        "messaging",
        "messages",
        context.conversationId
      ];
      queryClient.setQueryData(messagesKey, (old) => {
        const existing = old?.messages ?? [];
        const flagged = existing.map((m) => {
          if (m.clientMessageId === context.clientMessageId) {
            return { ...m, status: "failed" };
          }
          return m;
        });
        return { messages: flagged, hasMore: old?.hasMore ?? false };
      });
      const apiError = error;
      const details = apiError?.details ?? {};
      emitAnalytics("messaging_message_send_failed", {
        conversation_id: context.conversationId,
        error_code: apiError?.code ?? "UNKNOWN",
        error_status: details.status,
        message_length: input.content?.length ?? 0
      });
    }
  });
  const sendMessage = useCallback3(
    (input) => {
      mutation.mutate({ ...input, clientMessageId: generateClientMessageId() });
    },
    [mutation.mutate]
  );
  const sendMessageAsync = useCallback3(
    (input) => mutation.mutateAsync({ ...input, clientMessageId: generateClientMessageId() }),
    [mutation.mutateAsync]
  );
  return {
    sendMessage,
    sendMessageAsync,
    isLoading: mutation.isPending,
    error: mutation.error ?? null
  };
}

// src/hooks/messaging/useCreateConversation.ts
import { useMutation as useMutation7, useQueryClient as useQueryClient6 } from "@tanstack/react-query";
import {
  CreateConversationResultDto
} from "@dramadetector/shared/schemas";
function mapConversationDtoToConversation(dto) {
  return {
    id: dto.id,
    user_id: dto.user_id,
    title: dto.title ?? null,
    status: dto.status,
    tier_required: dto.tier_required,
    last_message_at: dto.last_message_at ?? null,
    created_at: dto.created_at,
    updated_at: dto.updated_at
  };
}
async function postCreateConversation(input) {
  const result = await fetchJsonValidated(
    CreateConversationResultDto,
    getEdgeFunctionUrl("create-conversation"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: input.title ?? null })
    }
  );
  return mapConversationDtoToConversation(result.conversation);
}
function useCreateConversation() {
  const queryClient = useQueryClient6();
  const mutation = useMutation7({
    mutationFn: (input) => postCreateConversation(input ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["messaging", "conversations"]
      });
    }
  });
  return {
    createConversation: (input) => {
      mutation.mutate(input);
    },
    createConversationAsync: (input) => mutation.mutateAsync(input),
    isLoading: mutation.isPending,
    error: mutation.error ?? null,
    data: mutation.data
  };
}

// src/hooks/messaging/useMarkMessagesRead.ts
import { useMutation as useMutation8, useQueryClient as useQueryClient7 } from "@tanstack/react-query";
import { MarkMessagesReadResultDto } from "@dramadetector/shared/schemas";
async function postMarkMessagesRead(input) {
  await fetchJsonValidated(
    MarkMessagesReadResultDto,
    getEdgeFunctionUrl("mark-messages-read"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        conversationId: input.conversationId,
        messageIds: input.messageIds
      })
    }
  );
}
function useMarkMessagesRead() {
  const queryClient = useQueryClient7();
  const mutation = useMutation8({
    mutationFn: (input) => postMarkMessagesRead(input),
    onMutate: async (input) => {
      const messagesKey = ["messaging", "messages", input.conversationId];
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const previousCache = queryClient.getQueryData(messagesKey);
      const nowIso = (/* @__PURE__ */ new Date()).toISOString();
      const idSet = new Set(input.messageIds);
      queryClient.setQueryData(messagesKey, (old) => {
        const existing = old?.messages ?? [];
        const updated = existing.map(
          (m) => idSet.has(m.id) ? { ...m, readAt: nowIso } : m
        );
        return { messages: updated, hasMore: old?.hasMore ?? false };
      });
      return { conversationId: input.conversationId, previousCache };
    },
    onError: (_error, _input, context) => {
      if (!context?.previousCache) return;
      const messagesKey = [
        "messaging",
        "messages",
        context.conversationId
      ];
      queryClient.setQueryData(messagesKey, context.previousCache);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["messaging", "conversations"]
      });
    }
  });
  return {
    markMessagesRead: (input) => {
      mutation.mutate(input);
    },
    isLoading: mutation.isPending,
    error: mutation.error ?? null
  };
}

// src/utils/auth-error.ts
var AUTH_REQUIRED_MESSAGE_RE = /valid authentication token required|authentication required|^unauthorized$/i;
var getAuthFailureReason = (error) => {
  if (!error || typeof error !== "object") return null;
  const e = error;
  if (typeof e.authFailureReason === "string") return e.authFailureReason;
  if (typeof e.details?.authFailureReason === "string")
    return e.details.authFailureReason;
  if (e.code === "AUTH_BROKEN") return "auth_broken";
  return null;
};
var classifyAuthFailureReason = (reason) => {
  if (!reason) return null;
  if (reason === "auth_broken") return "auth_broken";
  if (reason === "config_missing") return "config_missing";
  if (reason === "missing_bearer_token" || reason === "missing_token") {
    return "missing_bearer";
  }
  if (reason === "invalid_claims" || reason === "invalid_signature_or_jwks" || reason === "jwt_validation_failed" || reason === "opaque_token_invalid") {
    return "invalid_jwt";
  }
  if (reason === "anonymous_strict_route" || reason === "service_role_not_allowed") {
    return "strict_route";
  }
  return "other_auth_failure";
};
var getAuthFailureCategory = (error) => {
  return classifyAuthFailureReason(getAuthFailureReason(error));
};
var isAuthRequiredError = (error) => {
  if (typeof error === "string") return AUTH_REQUIRED_MESSAGE_RE.test(error);
  if (!error || typeof error !== "object") return false;
  const e = error;
  if (e.status === 401 || e.details?.status === 401 || e.code === "UNAUTHORIZED")
    return true;
  const msg = typeof e.message === "string" ? e.message : String(e);
  return AUTH_REQUIRED_MESSAGE_RE.test(msg);
};
function isCancelledError(err) {
  if (!err || typeof err !== "object") return false;
  const e = err;
  if (e.silent === true) return true;
  if (e.message === "CancelledError" && (e.name === "Error" || e.name === "CancelledError"))
    return true;
  return false;
}
function isTransientFetchError(err) {
  if (!err || typeof err !== "object") return false;
  const e = err;
  if (e.code === "HISTORY_ERROR" && typeof e.details === "object" && e.details !== null) {
    const d = e.details;
    if (d.name === "TypeError" && typeof d.message === "string" && d.message.includes("Failed to fetch")) {
      return true;
    }
  }
  return false;
}

// src/utils/response-transformer.ts
import { z } from "zod";
import { decode } from "he";
function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
function decodeHtmlEntities(value) {
  return decode(value);
}
function decodeObjectValues(obj) {
  if (obj === null || obj === void 0) return obj;
  if (typeof obj === "string") return decodeHtmlEntities(obj);
  if (Array.isArray(obj)) return obj.map(decodeObjectValues);
  if (typeof obj === "object") {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = decodeObjectValues(value);
    }
    return result;
  }
  return obj;
}
function transformKeysToCamel(obj) {
  if (obj === null || obj === void 0) return obj;
  if (Array.isArray(obj)) return obj.map(transformKeysToCamel);
  if (typeof obj === "object") {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[snakeToCamel(key)] = transformKeysToCamel(value);
    }
    return result;
  }
  return obj;
}
function hasSnakeCaseKeys(obj) {
  if (typeof obj !== "object" || obj === null) return false;
  if (Array.isArray(obj)) return obj.some(hasSnakeCaseKeys);
  const keys = Object.keys(obj);
  if (keys.some((key) => key.includes("_"))) return true;
  return Object.values(obj).some(hasSnakeCaseKeys);
}
var IssueSeveritySchema = z.enum(["low", "medium", "high", "critical"]);
var IssueTypeSchema = z.enum([
  "passive-aggressive",
  "blame-shifting",
  "emotional-manipulation",
  "absolutes",
  "dismissive",
  "aggressive-tone",
  "defensiveness",
  "contempt"
]);
var DramaIssueSchema = z.object({
  type: IssueTypeSchema,
  severity: IssueSeveritySchema,
  description: z.string(),
  snippet: z.string(),
  startIndex: z.number(),
  endIndex: z.number()
});
var SuggestedRewriteSchema = z.object({
  original: z.string(),
  // Accept either field name for backward compat with warm-cached responses
  rewritten: z.string().optional(),
  suggested: z.string().optional(),
  reasoning: z.string().optional(),
  explanation: z.string().optional(),
  improvementAreas: z.array(z.string()).optional(),
  tone: z.string().optional()
}).transform((val) => ({
  original: val.original,
  rewritten: val.rewritten || val.suggested || "",
  reasoning: val.reasoning || val.explanation || "",
  improvementAreas: val.improvementAreas || [],
  tone: val.tone
}));
var PatternSchema = z.object({
  type: z.string(),
  frequency: z.number(),
  examples: z.array(z.string()),
  description: z.string()
});
var TransformationSchema = z.object({
  transformationType: z.enum([
    "toneShift",
    "clarityBoost",
    "confidenceAdjustment",
    "defensiveReduction"
  ]),
  beforeText: z.string(),
  afterText: z.string(),
  insights: z.array(z.string()),
  impactScore: z.number()
});
var CacheMetricsSchema = z.object({
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  hitRate: z.number()
});
var AnalysisMetadataSchema = z.object({
  provider: z.enum(["groq", "openai", "anthropic"]),
  model: z.string(),
  latencyMs: z.number(),
  tokensUsed: z.number().optional(),
  costUsd: z.number().optional(),
  cached: z.boolean(),
  timestamp: z.string(),
  cacheMetrics: CacheMetricsSchema.optional()
});
var TrialInfoSchema = z.object({
  remaining: z.number()
});
var DramaAnalysisResponseSchema = z.object({
  id: z.string(),
  score: z.number(),
  category: z.enum(["safe", "caution", "high_drama", "extreme_drama"]),
  issues: z.array(DramaIssueSchema),
  rewrites: z.array(SuggestedRewriteSchema),
  summary: z.string(),
  metadata: AnalysisMetadataSchema,
  trial: TrialInfoSchema.optional(),
  patterns: z.array(PatternSchema).optional(),
  transformations: z.array(TransformationSchema).optional(),
  /** DD-614: ISO 639-1 language code of the analyzed text */
  languageCode: z.string().optional()
});
function normalizeResponse(schema, data) {
  const normalized = decodeObjectValues(
    hasSnakeCaseKeys(data) ? transformKeysToCamel(data) : data
  );
  return schema.parse(normalized);
}
function safeNormalizeResponse(schema, data) {
  const normalized = decodeObjectValues(
    hasSnakeCaseKeys(data) ? transformKeysToCamel(data) : data
  );
  const result = schema.safeParse(normalized);
  if (result.success) return { success: true, data: result.data };
  return { success: false, error: result.error };
}

// src/utils/offline.ts
import NetInfo from "@react-native-community/netinfo";
import { onlineManager, focusManager } from "@tanstack/react-query";
import { AppState, Platform } from "react-native";
function setupNetworkListener() {
  onlineManager.setEventListener((setOnline) => {
    return NetInfo.addEventListener((state) => {
      setOnline(!!state.isConnected);
    });
  });
}
function setupFocusListener() {
  const subscription = AppState.addEventListener(
    "change",
    (status) => {
      if (Platform.OS !== "web") {
        focusManager.setFocused(status === "active");
      }
    }
  );
  return () => subscription.remove();
}
function setupOfflineSupport() {
  setupNetworkListener();
  const cleanupFocus = setupFocusListener();
  return cleanupFocus;
}
async function isOnline() {
  const state = await NetInfo.fetch();
  return !!state.isConnected;
}
function isQueryClientOnline() {
  return onlineManager.isOnline();
}

// src/hooks/useNetworkStatus.ts
import { useState, useEffect } from "react";
import NetInfo2, { NetInfoStateType } from "@react-native-community/netinfo";
import { onlineManager as onlineManager2 } from "@tanstack/react-query";
function useNetworkStatus() {
  const [networkState, setNetworkState] = useState({
    type: NetInfoStateType.other,
    isConnected: true,
    isInternetReachable: null,
    details: null
  });
  useEffect(() => {
    NetInfo2.fetch().then(setNetworkState);
    const unsubscribe = NetInfo2.addEventListener(setNetworkState);
    return () => {
      unsubscribe();
    };
  }, []);
  return {
    isConnected: networkState.isConnected ?? false,
    isOnline: onlineManager2.isOnline(),
    type: networkState.type,
    isInternetReachable: networkState.isInternetReachable,
    isOffline: !(networkState.isConnected ?? false)
  };
}

// src/hooks/useAcceptTos.ts
import { useMutation as useMutation9 } from "@tanstack/react-query";
import {
  AcceptTosResultDto
} from "@dramadetector/shared/schemas";
async function acceptTos(request) {
  return fetchJsonValidated(
    AcceptTosResultDto,
    getEdgeFunctionUrl("accept-tos"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tos_version: request.tosVersion,
        acceptance_method: request.acceptanceMethod ?? "modal",
        ...request.trainingConsent !== void 0 && {
          training_consent: request.trainingConsent
        }
      })
    }
  );
}
function useAcceptTos() {
  return useMutation9({
    mutationFn: acceptTos,
    onError: onMutationError("useAcceptTos", "accept-tos")
  });
}
export {
  BetterAuthAdapter,
  DEFAULT_CONVERSATIONS_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  DramaAnalysisResponseSchema,
  DtoValidationError,
  HttpError,
  QUERY_CACHE_KEY,
  acceptTos,
  analyzeMessage,
  analyzeMessageAsNarrativeClarityEnvelope,
  authenticatedFetch,
  camelToSnake,
  classifyAuthFailureReason,
  clearAllAnalysisHistory,
  completeRevision,
  configureAuthToken,
  createApiError,
  createQueryClient,
  createQueryPersister,
  createRevision,
  createRewriteEvent,
  decodeHtmlEntities,
  decodeJwtPayload,
  decodeObjectValues,
  dehydrateOptions,
  deleteAnalysis,
  deleteUserData,
  ensureStringMessage,
  fetchJsonValidated,
  getAnalysisById,
  getAnalysisHistory,
  getAnalysisStats,
  getAuthFailureCategory,
  getAuthFailureReason,
  getAuthStatus,
  getClientConfig,
  getEdgeFunctionUrl,
  getErrorMessage,
  getGeneratedContent,
  getQuotaBalance,
  getQuotaLedgerEntry,
  getQuotaLedgerHistory,
  getSentryTraceHeaders,
  getUserStatsQuota,
  hasSnakeCaseKeys,
  initClientConfig,
  insertTosAcceptance,
  isApiError,
  isAuthRequiredError,
  isCancelledError,
  isOnline,
  isQueryClientOnline,
  isTransientFetchError,
  mapBetterAuthSession,
  mapBetterAuthUser,
  mapSupabaseError,
  mergeGeneratedContent,
  mutationKeys,
  normalizeResponse,
  queryKeys,
  requestQuotaRefund,
  rescoreAnalysis,
  resetAuthToken,
  resetClientConfig,
  safeNormalizeResponse,
  setAuthStatus,
  setOnAuthError,
  setupFocusListener,
  setupNetworkListener,
  setupOfflineSupport,
  snakeToCamel,
  toNarrativeClarityAnalysisEnvelope,
  transformAnalysisRecord,
  transformKeysToCamel,
  useAcceptTos,
  useAnalysisHistory,
  useAnalysisStats,
  useAnalyzeMutation,
  useAuthReady,
  useConversations,
  useCreateConversation,
  useCrisisAssistance,
  useDeleteUserData,
  useGeneratedContent,
  useInfiniteAnalysisHistory,
  useMarkMessagesRead,
  useMessages,
  useNetworkStatus,
  usePractitionerCrisisConsent,
  usePractitionerGroups,
  usePractitionerInvite,
  usePractitionerRemove,
  useQuotaBalance,
  useQuotaLedgerEntry,
  useQuotaLedgerHistory,
  useSendMessage
};
